import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, EffectCoverflow } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";

const TelecallingServices = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const staggerContainer = {
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const servicePillars = [
    {
      title: "Targeted Campaigns",
      icon: "🎯",
      features: [
        "AI-driven audience segmentation",
        "Hyper-personalized messaging",
        "Dynamic script optimization",
        "Real-time performance tracking",
        "Multi-channel integration",
      ],
      color: "from-blue-500 to-blue-700",
    },
    {
      title: "Lead Qualification",
      icon: "🔍",
      features: [
        "BANT qualification framework",
        "Custom scoring models",
        "Intent detection algorithms",
        "CRM integration",
        "Priority routing",
      ],
      color: "from-purple-500 to-purple-700",
    },
    {
      title: "Event Support",
      icon: "🎤",
      features: [
        "Pre-event awareness calls",
        "Personalized invitations",
        "Attendance confirmation",
        "Post-event follow-ups",
        "Feedback collection",
      ],
      color: "from-green-500 to-green-700",
    },
  ];

  const successStories = [
    {
      id: 1,
      metric: "160%",
      title: "Lead Generation Boost",
      description: "For healthcare provider's new service line",
    },
    {
      id: 2,
      metric: "45%",
      title: "Conversion Rate Increase",
      description: "Through optimized lead nurturing",
    },
    {
      id: 3,
      metric: "70%",
      title: "Event Attendance Growth",
      description: "For technology conference outreach",
    },
    {
      id: 4,
      metric: "3:1",
      title: "ROI Achieved",
      description: "For financial services campaign",
    },
  ];

  const technologyStack = [
    {
      name: "Predictive Dialer",
      description: "Automated call distribution for maximum agent efficiency",
      icon: "📱",
    },
    {
      name: "Conversation AI",
      description: "Real-time call analytics and agent assistance",
      icon: "🧠",
    },
    {
      name: "CRM Integration",
      description: "Seamless connection with your existing systems",
      icon: "🔄",
    },
    {
      name: "Sentiment Analysis",
      description: "Emotion detection for better engagement",
      icon: "😊",
    },
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-28 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1543269664-76bc3997d9ea?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center opacity-20"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-serif"
          >
            Telecalling Services
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl max-w-3xl mx-auto"
          >
            Transform prospects into customers with our data-driven outreach
          </motion.p>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-serif">
              <span className="relative pb-2 border-b-2 border-orange-500 inline-block">
                Precision Telecalling Solutions
              </span>
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              At Augere Strategies , we combine human expertise with
              cutting-edge technology to deliver telecalling campaigns that
              convert. Our approach goes beyond basic cold calling to create
              meaningful conversations that drive business growth.
            </p>
            <div className="space-y-4">
              {[
                "500K+ decision-maker contacts",
                "85% call answer rate",
                "Multilingual calling teams",
                "Real-time campaign analytics",
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex items-start"
                >
                  <svg
                    className="h-6 w-6 text-blue-500 mr-3 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700 font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div variants={fadeInUp} className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl opacity-20 blur-lg"></div>
            <div className="relative bg-white p-1 rounded-xl shadow-xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1556740738-b6a63e27c4df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                alt="Telecalling team"
                className="w-full h-auto rounded-lg transform hover:scale-105 transition-transform duration-500"
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Service Pillars */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif"
            >
              Our Telecalling Expertise
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Comprehensive solutions for every outreach need
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {servicePillars.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -10 }}
                className={`bg-gradient-to-br ${service.color} text-white p-8 rounded-xl shadow-lg overflow-hidden relative group h-full`}
              >
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-all duration-300"></div>
                <div className="relative z-10 h-full flex flex-col">
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <svg
                          className="h-5 w-5 text-white mr-2 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif"
            >
              Our Technology Advantage
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Cutting-edge tools that enhance calling effectiveness
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {technologyStack.map((tech, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5 }}
                className="bg-white p-6 rounded-xl shadow-md text-center"
              >
                <div className="text-4xl mb-4">{tech.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {tech.name}
                </h3>
                <p className="text-gray-600">{tech.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif"
            >
              Our Proven Process
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              A systematic approach to telecalling success
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-8">
            {[
              {
                step: "1",
                title: "Strategy Session",
                description: "Define goals and target audience",
              },
              {
                step: "2",
                title: "Data Analysis",
                description: "Identify high-potential prospects",
              },
              {
                step: "3",
                title: "Script Development",
                description: "Create compelling conversation flows",
              },
              {
                step: "4",
                title: "Campaign Launch",
                description: "Execute targeted calling program",
              },
              {
                step: "5",
                title: "Optimization",
                description: "Refine based on performance data",
              },
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif"
            >
              Success Stories
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Measurable results we've delivered for clients
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <Swiper
              effect={"coverflow"}
              grabCursor={true}
              centeredSlides={true}
              slidesPerView={"auto"}
              coverflowEffect={{
                rotate: 0,
                stretch: 0,
                depth: 100,
                modifier: 2.5,
                slideShadows: false,
              }}
              loop={true}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              breakpoints={{
                640: {
                  coverflowEffect: {
                    rotate: 0,
                    stretch: -50,
                    depth: 100,
                    modifier: 1,
                  },
                },
                1024: {
                  coverflowEffect: {
                    rotate: 0,
                    stretch: -100,
                    depth: 200,
                    modifier: 1.5,
                  },
                },
              }}
              modules={[EffectCoverflow, Autoplay, Pagination]}
              className="w-full pb-12 md:pb-16"
            >
              {successStories.map((story) => (
                <SwiperSlide
                  key={story.id}
                  className="w-[280px] sm:w-[320px] md:w-[350px] lg:w-[380px]"
                >
                  <motion.div
                    whileHover={{ y: -10 }}
                    className="h-full bg-white p-6 rounded-xl shadow-md border border-gray-100"
                  >
                    <div className="text-4xl font-bold text-blue-600 mb-3">
                      {story.metric}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {story.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{story.description}</p>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Ready to Transform Your Outreach?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl mb-8 max-w-3xl mx-auto"
          >
            Let's discuss how our telecalling services can drive your business
            growth.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row justify-center gap-4"
          >
            <a
              href="tel:+441416282052"
              className="px-8 py-3 bg-white hover:bg-gray-100 text-blue-600 font-medium rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              +44 1416 282 052
            </a>
            <a
              href="#contact"
              className="px-8 py-3 border-2 border-white text-white font-medium rounded-lg transition-all duration-300 hover:bg-white hover:text-blue-600"
            >
              Get a Quote
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TelecallingServices;
